'use client';

import { Box } from '@mui/material';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { UploadFile } from '@/types';

interface PhoneImageGalleryProps {
  images: UploadFile[];
  alt: string;
}

const TRANSITION_TIME = 5000;
const DEFAULT_WIDTH = 270;
const DEFAULT_HEIGHT = 528;

export default function PhoneImageGallery({ images, alt }: PhoneImageGalleryProps) {
  const [currentImg, setCurrentImg] = useState(0);
  const requestRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);
  const previousTimeRef = useRef<number>(0);

  const incrementCurrent = useCallback(() => {
    setCurrentImg((currentVal) => {
      const newValue = currentVal + 1;
      return newValue >= images.length ? 0 : newValue;
    });
  }, [images]);

  const animate = (time: number) => {
    if (previousTimeRef.current != undefined) {
      const deltaTime = time - previousTimeRef.current;
      if (deltaTime > TRANSITION_TIME) {
        incrementCurrent();
        previousTimeRef.current = time;
      }
    }
    requestRef.current = requestAnimationFrame(animate);
  };
  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef?.current ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Make sure the effect runs only once

  return (
    <Box
      data-component="PhoneImageGallery"
      aria-label={alt}
      sx={(theme) => ({
        position: 'relative',
        borderRadius: '2rem',
        overflow: 'hidden',
        width: `${DEFAULT_WIDTH + 35}px`,
        height: `${DEFAULT_HEIGHT + 25}px`,
        margin: '0 auto',
        '.gallery-wrapper': {
          position: 'relative',
          zIndex: 100,
        },
        '.gallery-images': {
          position: 'absolute',
          left: 16,
          right: 16,
          top: 12,
          bottom: 12,
          background: '#000100', //exact black from image
          borderRadius: '2rem',
          overflow: 'hidden',
        },
        ' .gallery-image': {
          background: theme.palette.purple['800'],
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          opacity: 0,
          transition: 'transform 1s ease-out',
          '@media (prefers-reduced-motion)': {
            transition: 'opacity 0.5s ease-out',
          },
          '&--prev': {
            opacity: 1,
            transform: 'translate(-50%, -150%)',
          },
          '&--current': {
            opacity: 1,
            transform: 'translate(-50%, -50%)',
          },
          '&--next': {
            opacity: 0,
            transform: 'translate(-50%, 50%)',
          },
        },
      })}
    >
      <Image
        className="gallery-wrapper"
        src="/assets/PhoneChrome.png"
        width={DEFAULT_WIDTH + 35}
        height={DEFAULT_HEIGHT + 25}
        alt=""
        role="presentation"
      />
      <Box
        className="gallery-images"
        {...{
          'data-current-img': currentImg,
        }}
      >
        {images.map((img, index) => {
          const imgSrc = getImageUrl({ image: img, size: 'url' });
          if (!imgSrc) return null;
          const isExternalUrl = imgSrc.startsWith('http://') || imgSrc.startsWith('https://');

          return (
            <Image
              key={`${imgSrc}${index}`}
              className={`gallery-image ${
                index === currentImg - 1 || (currentImg === 0 && index === images.length - 1)
                  ? 'gallery-image--prev'
                  : ''
              } ${index === currentImg ? 'gallery-image--current' : ''} ${
                index === currentImg + 1 || (currentImg === images.length - 1 && index === 0)
                  ? 'gallery-image--next'
                  : ''
              }`}
              src={imgSrc}
              alt={img.alternativeText || 'Landing page hero media'}
              width={DEFAULT_WIDTH}
              height={DEFAULT_HEIGHT}
              unoptimized={isExternalUrl}
            />
          );
        })}
      </Box>
    </Box>
  );
}
